import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { BlendEvent, ErrCode, ErrorCode, FileItem } from 'src/app/shared/models';
import { FileListService } from '../services/file-list.service';
import { ValidateService } from 'src/app/core/validate.service';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { BatchRenameService } from 'src/app/shared/services/batch-rename.service';
import { TextFieldComponent } from '@material-library';
import { FeatureService } from 'src/app/shared/services/feature.service';
import { AtomicRenameService } from 'src/app/shared/services/atomic-rename.service';
import { IBatchActionView } from 'src/sync';
import { BlendService } from 'src/app/shared/services/blend.service';
import { NotificationsService } from 'src/app/core/notifications.service';
import { UserService } from 'src/app/core/user.service';

@Component({
  selector: 'app-dialog-rename',
  templateUrl: './dialog-rename.component.html',
  styleUrls: ['./dialog-rename.component.scss']
})
export class DialogRenameComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @ViewChild('fileNameInput') fileNameInput: TextFieldComponent;

  @Input()
  isOpen: boolean = false;

  @Output()
  isOpenChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input()
  public Item: FileItem;

  @Output()
  renameSuccess: EventEmitter<any> = new EventEmitter<any>();

  public Title: string = '';

  public errcode: ErrCode;
  public nameInvalidError: string = '';
  public newItemName: string = '';
  public isActionDisable: boolean = false;
  public errors: Array<ErrorCode>;
  public fileList: FileItem[] = [];
  private fileListSubscription: Subscription;
  public isLinkPreserveAllowed: boolean;
  public isRenameProcessing = false;
  public batchView: IBatchActionView = {
    spinner: 0,
    total: 0,
    completed: 0,
    status: undefined,
    globalCancel: false,
    percent: 0,
    completedBatch: [],
    errors: [],
    action: 0,
    syncIds: []
  };
  private subscription: Subscription;
  public itemName: string = '';
  public extensionIndex: number;
  public fileExtension: string;

  constructor(
    private fileListService: FileListService,
    private validateService: ValidateService,
    private snackbarService: SnackbarService,
    private batchRenameService: BatchRenameService,
    private featureService: FeatureService,
    private atomicRenameService: AtomicRenameService,
    private blendService: BlendService,
    private notificationsService: NotificationsService,
    private userService: UserService
  ) {
    // Temporary addition to handle feature rollout.
    // This will be removed once the feature is released to all users.
    this.setLinkPreserveFlag();
  }

   // Getter for the condition
   get isDirRenameWithPreserveAllowed(): boolean {
    return this.isLinkPreserveAllowed && this.isRenameProcessing && this.Item?.type === 'dir';
  }

  async setLinkPreserveFlag() {
    this.isLinkPreserveAllowed = await this.featureService.isAllowed('linkPreserve') || this.userService.get('has_beta_access') == 1;
    this.batchView = this.isLinkPreserveAllowed ? this.atomicRenameService.view : this.batchRenameService.view;
  }
  
  ngOnChanges() {
    this.Title = ` "${this.Item?.name}"`
  }
  
  ngOnInit(): void {
    this.extensionIndex = this.Item.name.lastIndexOf('.');
    if (this.extensionIndex > -1 && this.Item.type === 'file') {
        this.itemName = this.Item.name.substring(0, this.extensionIndex);
        this.fileExtension = this.Item.name.substring(this.extensionIndex + 1, this.Item.name.length);
    } else {
        this.itemName = this.Item.name;
    }
    this.errors = [];
    this.fileListSubscription = this.fileListService.getListSubscription().subscribe((data) => {
      if (data && data.pathlist.length) {
        this.fileList = data.pathlist;
      }
    });

    this.subscription = this.atomicRenameService.percent$.subscribe((newPercent) => {
      this.batchView.percent = newPercent;
    });
  }

  ngAfterViewInit(): void {
    const input = this.fileNameInput?.input as unknown as QueryList<ElementRef<HTMLInputElement>>;
    if (input) {
      input.first.nativeElement.focus();
    }
  }

  ngOnDestroy(): void {
    if (this.fileListSubscription) {
      this.fileListSubscription.unsubscribe();
    }
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  onRenameClick = async () => {
    if(this.newItemName === ''){
      this.closeDialog();
      return;
    }
    this.isRenameProcessing = true;
    await this.onRenameSave();
    this.isRenameProcessing = false;
    if (!this.errors.length) {
      this.isOpen = false;
      setTimeout(() => {
        this.isOpenChange.next(false);
      }, 0);
    }
  }

  onRenameSave = async () => {
    this.blendService.track(BlendEvent.BUTTON_CLICKED, { button_text: 'confirm rename' });
    this.notificationsService.stopNotificationLoop();
    try {
      if (this.isValid()) {
        let response;
        if (this.isLinkPreserveAllowed) {
          response = await this.atomicRenameService.atomicRename(
            this.Item.sync_id,
            this.newItemName,
            this.Item.is_publink
          );
        } else {
          response = this.Item.type === 'dir'
            ? await this.batchRenameService.execute(
              this.Item.sync_id,
              this.Item.pid,
              this.newItemName
            )
            : await this.batchRenameService.executeFile(
              this.Item.sync_id,
              this.Item.pid,
              this.newItemName
            );
        }
        this.blendService.track(BlendEvent.RENAME_ITEM, {
          size: this.Item.size,
          type:
            this.Item.type.toUpperCase() === 'DIR'
              ? 'folder'
              : 'file',
          mime_type: this.Item.mime_type,
        });
        this.renameSuccess.emit(response);
        this.snackbarService.showMessage('success', `"${this.newItemName}" was successfully renamed.`);
        this.notificationsService.startNotificationLoop();
      } else {
        this.snackbarService.showMessage('critical', "Couldn't complete action. Try again.");
      }
    } catch (err) {
      this.errors.push({ code: err.code });
      this.errcode = ErrCode.fromException(err);
    }
  };
  

  closeDialog = () => {
    this.isOpen = false;
    setTimeout(() => {
      this.isOpenChange.next(false);
    }, 0);
    this.nameInvalidError = '';
    this.errcode = null;
    this.errors = [];
  }

  closeDialogWithProgress = () => {
    const c = window.confirm('Are you sure you want to close this? This will continue to process after closing.');
    if (c) {
      this.isOpen = false;
      this.snackbarService.showMessage('default', 'The action will continue processing until finished.')
    } 
  }

  getItemName = (itemName: string) => {
    this.errors = [];
    if (this.extensionIndex > -1 && this.Item.type === 'file') {
      this.newItemName = [itemName, this.fileExtension].join('.');
    } else {
      this.newItemName = itemName
    }
    this.isActionDisable = false;
    this.validInput(itemName);
  };

  validInput(displayName: string): string {
    if (!this.validateService.illegalCharacters(displayName)) {
      this.isActionDisable = true;
      this.nameInvalidError = 'The following characters are not allowed: \ / : ? * < > ” |'
    } else if (!this.validateService.empty(displayName)) {
      this.isActionDisable = true;
      this.nameInvalidError = 'Enter a valid file name.';
    }else if(!this.validateService.leadingSpace(displayName)){
      this.isActionDisable = true;
      this.nameInvalidError = 'Files cannot begin with a space.';
    }else if(!this.validateService.trailingChar(displayName)){
      this.isActionDisable = true;
      this.nameInvalidError = 'Files cannot end in a . or a space.';
    }
     else {
      this.nameInvalidError = '';
    }
    return this.nameInvalidError
  }

  public isValid(): boolean {
    if (this.fileExists(this.newItemName)) {
      this.nameInvalidError = 'Enter a valid file name.';
      throw { code: 8211 };
    }
    return true;
  }

  public fileExists(fileName: string): boolean {
    if (!fileName || !this.fileList) {
      return false;
    }
    return this.fileList.some(f => {
      return f.name.toLowerCase() === fileName.toLowerCase();
    });
  }

}
